import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PRISMAX_BACKEND_URL } from '../../../../../configs/config';
import { SwapMainAppContext } from '../../../../../context/SwapMainAppContext';
import { getApiErrorMessage, isSessionExpiredError } from '../../../../../utils/authErrors';
import { formatSentAt } from './formatSentAt';
import styles from './SendEmailModal.module.css';

const CHUNK_SIZE = 20;

export default function SendEmailModal({
    title, note, actionLabel = 'Send', adminToken, recipientsPath, sendPath, onClose, onSent,
}) {
    const context = useContext(SwapMainAppContext);
    const handleAdminSessionExpired = context?.handleAdminSessionExpired;
    const [includeSent, setIncludeSent] = useState(false);
    const [recipients, setRecipients] = useState([]);
    const [selected, setSelected] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);
    const [progress, setProgress] = useState(0);
    const [summary, setSummary] = useState(null);
    const didSend = useRef(false);

    const callApi = useCallback(async (path, options = {}) => {
        const response = await fetch(`${PRISMAX_BACKEND_URL}${path}`, {
            ...options,
            headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
            const message = getApiErrorMessage(result, 'Request failed.');
            if (isSessionExpiredError(message)) handleAdminSessionExpired?.();
            throw new Error(message);
        }
        return result;
    }, [adminToken, handleAdminSessionExpired]);

    const handleClose = useCallback(() => {
        if (didSend.current) onSent?.();
        onClose();
    }, [onClose, onSent]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        callApi(`${recipientsPath}?include_sent=${includeSent}`)
            .then((result) => {
                if (cancelled) return;
                const groups = new Map();
                result.data.forEach((row) => {
                    const key = row.email.toLowerCase();
                    if (!groups.has(key)) groups.set(key, { key, email: row.email, name: row.name, ids: [], sentDates: [] });
                    const group = groups.get(key);
                    group.name = group.name || row.name;
                    group.ids.push(row.id);
                    if (row.sent_at) group.sentDates.push(row.sent_at);
                });
                const grouped = Array.from(groups.values()).map((group) => ({
                    ...group,
                    hasSent: group.sentDates.length > 0,
                    sent_at: group.sentDates.length === group.ids.length ? group.sentDates.slice().sort().pop() : null,
                }));
                setRecipients(grouped);
                setSelected(new Set(grouped.map((group) => group.key)));
            })
            .catch((loadError) => {
                if (!cancelled) setError(loadError.message || 'Failed to load recipients.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [callApi, recipientsPath, includeSent]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && !sending) handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [sending, handleClose]);

    const toggleRecipient = (id) => {
        setSelected((previous) => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const sendSelected = async () => {
        const selectedGroups = recipients.filter((group) => selected.has(group.key));
        const chunks = [{ ids: [], groups: [] }];
        selectedGroups.forEach((group) => {
            let current = chunks[chunks.length - 1];
            if (current.ids.length && current.ids.length + group.ids.length > CHUNK_SIZE) {
                current = { ids: [], groups: [] };
                chunks.push(current);
            }
            current.ids.push(...group.ids);
            current.groups.push(group);
        });
        const resend = selectedGroups.some((group) => group.hasSent);
        const totals = { sent: 0, failed: [], skipped: 0, error: '' };
        setSending(true);
        setError('');
        setProgress(0);
        let processed = 0;
        try {
            for (const chunk of chunks) {
                const result = await callApi(sendPath, {
                    method: 'POST',
                    body: JSON.stringify({ ids: chunk.ids, include_sent: resend }),
                });
                didSend.current = true;
                const sentIds = new Set(result.data.sent_ids);
                const failedIds = new Set(result.data.failed.map((item) => item.id));
                chunk.groups.forEach((group) => {
                    if (group.ids.some((id) => sentIds.has(id))) totals.sent += 1;
                    else if (group.ids.some((id) => failedIds.has(id))) totals.failed.push(group.email);
                    else totals.skipped += 1;
                });
                processed += chunk.groups.length;
                setProgress(processed);
            }
        } catch (sendError) {
            totals.error = sendError.message || 'Sending stopped unexpectedly.';
        } finally {
            setSending(false);
            setSummary(totals);
        }
    };

    return createPortal(
        <div
            className={styles.overlay}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !sending) handleClose();
            }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="send-email-modal-title">
                <div className={styles.header}>
                    <h3 id="send-email-modal-title" className={styles.title}>{title}</h3>
                    <button type="button" className={styles.closeButton} aria-label="Close"
                        disabled={sending} onClick={handleClose}>×</button>
                </div>

                {summary ? (
                    <div className={styles.body}>
                        <p className={styles.summaryLine}>Sent to {summary.sent} {summary.sent === 1 ? 'recipient' : 'recipients'}.</p>
                        {summary.skipped ? <p className={styles.muted}>{summary.skipped} skipped (already sent or not eligible).</p> : null}
                        {summary.failed.length ? (
                            <div role="alert" className={styles.errorMsg}>
                                Delivery failed for {summary.failed.length}:
                                <ul className={styles.failedList}>
                                    {summary.failed.map((email) => <li key={email}>{email}</li>)}
                                </ul>
                            </div>
                        ) : null}
                        {summary.error ? <div role="alert" className={styles.errorMsg}>{summary.error}</div> : null}
                    </div>
                ) : (
                    <>
                        <div className={styles.controls}>
                            <button type="button" className={styles.linkButton} disabled={loading || sending}
                                onClick={() => setSelected(new Set(recipients.map((recipient) => recipient.key)))}>Select all</button>
                            <button type="button" className={styles.linkButton} disabled={loading || sending}
                                onClick={() => setSelected(new Set())}>Deselect all</button>
                            <label className={styles.toggle}>
                                <input type="checkbox" checked={includeSent} disabled={loading || sending}
                                    onChange={(event) => setIncludeSent(event.target.checked)} />
                                Include already sent
                            </label>
                            <span className={styles.count}>{selected.size} of {recipients.length} selected</span>
                        </div>
                        {note ? <p className={styles.note}>{note}</p> : null}
                        <div className={styles.body}>
                            {error ? <div role="alert" className={styles.errorMsg}>{error}</div> : null}
                            {loading ? <div className={styles.muted}>Loading recipients…</div> : null}
                            {!loading && !error && !recipients.length ? (
                                <div className={styles.muted}>No recipients to email.</div>
                            ) : null}
                            {!loading && recipients.length ? (
                                <ul className={styles.list}>
                                    {recipients.map((recipient) => (
                                        <li key={recipient.key}>
                                            <label className={styles.row}>
                                                <input type="checkbox" checked={selected.has(recipient.key)}
                                                    disabled={sending} onChange={() => toggleRecipient(recipient.key)} />
                                                <span className={styles.email}>{recipient.email}</span>
                                                {recipient.name ? <span className={styles.name}>{recipient.name}</span> : null}
                                                {recipient.sent_at ? (
                                                    <span className={styles.sentTag}>Sent {formatSentAt(recipient.sent_at)}</span>
                                                ) : null}
                                            </label>
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    </>
                )}

                <div className={styles.footer}>
                    {summary ? (
                        <button type="button" className={styles.primaryButton} onClick={handleClose}>Done</button>
                    ) : (
                        <>
                            <button type="button" className={styles.secondaryButton} disabled={sending}
                                onClick={handleClose}>Cancel</button>
                            <button type="button" className={styles.primaryButton}
                                disabled={loading || sending || !selected.size} onClick={sendSelected}>
                                {sending ? `Sending… ${progress}/${selected.size}` : `${actionLabel} to ${selected.size}`}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
